import { create } from 'zustand';
import { api } from '@/core/api/apiClient';
import type { Area, Stage, ProjectsViewType } from '@cg-projects/shared';
import { useFilterStore } from '@/features/filters/store/filterStore';
import type { GroupBy, SortBy } from '@/features/tasks/grouping';
import { nav } from '@/core/router/navIntent';

// filterStore's initial/reset arrangement — duplicated because filterStore keeps
// its DEFAULTS private; must match filterStore.ts. Used to tell "untouched" from
// "the user moved it" when a team default arrives after the route already applied.
const RESET_GROUP_BY: GroupBy = 'stage';
const RESET_SORT_BY: SortBy = 'priority';

/** The arrangement the last applyPrefs/persist left in the filter store, and for
 *  which project. Layout's persist effect fires on every groupBy/sortBy change
 *  and cannot tell a FilterBar gesture from an apply echo; persistPrefs compares
 *  against this so applied values (personal re-reads AND team defaults) are never
 *  re-minted as personal localStorage prefs. Module-scope bookkeeping, not
 *  renderable state. */
let synced: { areaId: number; groupBy: GroupBy; sortBy: SortBy } | null = null;

interface AreaState {
  areas: Area[];
  loading: boolean;
  /** TRUE once /areas has answered — success OR failure. Distinct from `loading`,
   *  which is false before the first fetch even starts. A route like #/p/12/board
   *  can only be judged "you can't see that project" AFTER this is true; checking
   *  earlier would reject every project deep link, 100% of the time. */
  areasLoaded: boolean;
  error: string | null;
  selectedAreaId: number | null;
  view: ProjectsViewType;
  stagesByArea: Record<number, Stage[]>;
  stagesLoading: Record<number, boolean>;

  loadAreas: (silent?: boolean) => Promise<void>;
  createArea: (name: string, memberIds: number[]) => Promise<Area>;
  selectArea: (id: number) => void;
  goHome: () => void;
  setView: (v: ProjectsViewType) => void;
  /** Write this project's remembered view/groupBy/sortBy. view lands only from
   *  setView (a real gesture); groupBy/sortBy land only when they MOVED since
   *  the last apply — never from a route apply, or a shared link (or a team
   *  default) would silently overwrite the recipient's saved preference. */
  persistPrefs: (includeView?: boolean) => void;
  // ---- route-apply variants: same state change, NO history intent, NO prefs
  // write-back, and no filter reset unless the project actually changed. Browser
  // Back inside a project must not wipe the filters the user is looking at.
  selectAreaFromRoute: (id: number, view?: ProjectsViewType) => void;
  goHomeFromRoute: () => void;
  setViewFromRoute: (v: ProjectsViewType) => void;
  /** Restore per-project prefs; a `preferView` from the URL wins over the stored
   *  one, and the project's team default fills only when neither named a view.
   *  groupBy/sortBy fall back the same way, PER KEY: personal stored value, else
   *  team default, else whatever the filter reset left. */
  applyPrefs: (id: number, preferView?: ProjectsViewType) => ProjectsViewType;
  loadStages: (areaId: number) => Promise<void>;
  /** Force-refetch (loadStages caches) — used after stage add/rename/reorder/delete. */
  reloadStages: (areaId: number) => Promise<void>;
  /** Replace one area in the list (after rename). */
  applyAreaUpdate: (area: Area) => void;
  /** Drop one area from the list (after archive); goes Home if it was selected. */
  removeArea: (id: number) => void;
}

export const useAreaStore = create<AreaState>()((set, get) => ({
  areas: [],
  loading: false,
  areasLoaded: false,
  error: null,
  selectedAreaId: null,
  view: 'board',
  stagesByArea: {},
  stagesLoading: {},

  loadAreas: async (silent = false) => {
    if (!silent) set({ loading: true, error: null });
    // The false→true flip is the cold-boot moment: only then can a route have
    // applied prefs against an EMPTY areas list (team arrangement invisible).
    // Later silent polls must not re-nudge — a user who gestured back to the
    // reset default with broken localStorage would be clobbered on a timer.
    const firstAnswer = !get().areasLoaded;
    try {
      const { areas } = await api.get<{ areas: Area[] }>('/areas');
      // error: null on EVERY success, including a silent poll. Only the non-silent
      // entry above clears it, and the sole non-silent caller is mount — so one
      // failed first load would otherwise pin `error` for the whole session and
      // make honest "couldn't load" branches fire on healthy data forever after.
      set({ areas, loading: false, areasLoaded: true, error: null });
      // No auto-select — land on the Home grid so the first screen isn't a firehose.
      if (firstAnswer) applyLateTeamArrangement();
    } catch (err) {
      // areasLoaded on the FAILURE path too: the answer is "we asked and can't
      // tell", which must still release a route waiting on validation rather
      // than hang it on a spinner forever.
      set({ areasLoaded: true });
      if (!silent) set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load areas.' });
    }
  },

  createArea: async (name, memberIds) => {
    const { area } = await api.post<{ area: Area }>('/areas', { name, memberIds });
    set((s) => ({ areas: [...s.areas, area].sort((a, b) => a.name.localeCompare(b.name)) }));
    get().selectArea(area.id);
    return area;
  },

  /** Restore this project's remembered view + organize prefs. `preferView` (from a
   *  URL segment) WINS over the stored view — a shared #/p/12/table must open Table. */
  applyPrefs: (id, preferView) => {
    // null = neither the URL nor this browser named a view — tracked as its own
    // state because 'board' can BE the personal choice; only genuine absence may
    // fall through to the project's team default below.
    let view: ProjectsViewType | null = preferView ?? null;
    // Personal presence is PER KEY: a user with a stored groupBy but no stored
    // sortBy keeps their groupBy and still gets the team sortBy below.
    let hasGroupBy = false;
    let hasSortBy = false;
    const f = useFilterStore.getState();
    try {
      const raw = localStorage.getItem(`cgp:area:${id}:prefs`);
      if (raw) {
        const p = JSON.parse(raw) as { view?: ProjectsViewType; groupBy?: string; sortBy?: string };
        if (view === null && (p.view === 'board' || p.view === 'table' || p.view === 'calendar' || p.view === 'history')) view = p.view;
        if (p.groupBy) { f.setGroupBy(p.groupBy as never); hasGroupBy = true; }
        if (p.sortBy) { f.setSortBy(p.sortBy as never); hasSortBy = true; }
      }
    } catch { /* prefs are best-effort */ }
    // Team defaults are READS here, never writes: they must not enter localStorage
    // via a route apply, or they would masquerade as a personal choice forever.
    // (On a cold boot `areas` can still be empty — the router's parked viewless
    // deep link re-runs this once /areas answers, and loadAreas nudges the
    // explicit-view case via applyLateTeamArrangement.)
    const area = get().areas.find((a) => a.id === id);
    if (!hasGroupBy && area?.defaultGroupBy) f.setGroupBy(area.defaultGroupBy);
    if (!hasSortBy && area?.defaultSortBy) f.setSortBy(area.defaultSortBy);
    const g = useFilterStore.getState();
    synced = { areaId: id, groupBy: g.groupBy, sortBy: g.sortBy };
    return view ?? area?.defaultView ?? 'board';
  },

  // ---- user gestures: these ARE navigation, so they push a history entry ----
  selectArea: (id) => {
    nav.mark('push');
    useFilterStore.getState().reset(); // clear search/assignee/show-completed per area
    const view = get().applyPrefs(id);
    set({ selectedAreaId: id, view });
    void get().loadStages(id);
  },

  goHome: () => {
    nav.mark('push');
    useFilterStore.getState().reset();
    set({ selectedAreaId: null });
  },

  // A view is a per-project PREFERENCE, not a place: replace, so leaving a project
  // costs one Back instead of one per view you happened to look at.
  setView: (v) => { nav.mark('replace'); set({ view: v }); get().persistPrefs(true); },

  // MERGES. `view` is only written when includeView is true (i.e. from setView, a
  // real user gesture) — otherwise opening a colleague's shared #/p/12/table link
  // would silently overwrite the recipient's own remembered view. groupBy/sortBy
  // are only written when they MOVED since the last apply/persist (`synced`):
  // Layout's persist effect fires on every arrangement change and cannot tell a
  // FilterBar gesture from an apply echo, so this comparison is the only thing
  // keeping a route-applied team default (or the mount echo) from being minted
  // as a personal pref for every project ever opened.
  persistPrefs: (includeView = false) => {
    const { selectedAreaId, view } = get();
    if (selectedAreaId === null) return;
    const key = `cgp:area:${selectedAreaId}:prefs`;
    try {
      const f = useFilterStore.getState();
      let stored: { view?: ProjectsViewType; groupBy?: string; sortBy?: string } = {};
      try { stored = JSON.parse(localStorage.getItem(key) ?? '{}') as typeof stored; } catch { /* fresh */ }
      const applied = synced?.areaId === selectedAreaId ? synced : null;
      const nextView = includeView ? view : stored.view;
      const nextGroupBy = applied && f.groupBy === applied.groupBy ? stored.groupBy : f.groupBy;
      const nextSortBy = applied && f.sortBy === applied.sortBy ? stored.sortBy : f.sortBy;
      if (nextView === stored.view && nextGroupBy === stored.groupBy && nextSortBy === stored.sortBy) return; // pure echo
      localStorage.setItem(key, JSON.stringify({
        ...(nextView ? { view: nextView } : {}),
        ...(nextGroupBy ? { groupBy: nextGroupBy } : {}),
        ...(nextSortBy ? { sortBy: nextSortBy } : {}),
      }));
      synced = { areaId: selectedAreaId, groupBy: f.groupBy, sortBy: f.sortBy };
    } catch { /* best-effort */ }
  },

  // ---- route apply: identical state change, but NOT a navigation ----
  selectAreaFromRoute: (id, view) => {
    // Only a genuine project CHANGE resets filters (same rule as a click). Opening
    // a task or flipping a view must leave the user's filters alone on Back.
    if (get().selectedAreaId !== id) useFilterStore.getState().reset();
    const v = get().applyPrefs(id, view);
    set({ selectedAreaId: id, view: v });
    void get().loadStages(id);
  },
  goHomeFromRoute: () => { useFilterStore.getState().reset(); set({ selectedAreaId: null }); },
  setViewFromRoute: (v) => set({ view: v }), // deliberately no persistPrefs

  loadStages: async (areaId) => {
    if (get().stagesByArea[areaId] || get().stagesLoading[areaId]) return;
    set((s) => ({ stagesLoading: { ...s.stagesLoading, [areaId]: true } }));
    try {
      const { stages } = await api.get<{ stages: Stage[] }>(`/areas/${areaId}/stages`);
      set((s) => ({
        stagesByArea: { ...s.stagesByArea, [areaId]: stages },
        stagesLoading: { ...s.stagesLoading, [areaId]: false },
      }));
    } catch {
      set((s) => ({ stagesLoading: { ...s.stagesLoading, [areaId]: false } }));
    }
  },

  reloadStages: async (areaId) => {
    try {
      const { stages } = await api.get<{ stages: Stage[] }>(`/areas/${areaId}/stages`);
      set((s) => ({ stagesByArea: { ...s.stagesByArea, [areaId]: stages } }));
    } catch { /* keep current */ }
  },

  applyAreaUpdate: (area) =>
    // memberCount and isMember are computed only by the LIST endpoint; single-area
    // responses (rename, lead change) omit them, and replacing wholesale would blank
    // the 🔒 badge (or hide Edit stages) the moment you renamed it. Keep what we know.
    set((s) => ({
      areas: s.areas
        .map((a) => (a.id === area.id ? { ...area, memberCount: area.memberCount ?? a.memberCount, isMember: area.isMember ?? a.isMember } : a))
        .sort((a, b) => a.name.localeCompare(b.name)),
    })),

  removeArea: (id) => {
    set((s) => ({ areas: s.areas.filter((a) => a.id !== id) }));
    // goHomeFromRoute, NOT goHome: being evicted from a project is not a
    // navigation the user made, so it must REPLACE. Pushing it would leave a
    // history entry that Back walks straight back into the revoked project.
    if (get().selectedAreaId === id) get().goHomeFromRoute();
  },
}));

/** Cold boot WITH an explicit view segment ('#/p/53/calendar'): applyRoute
 *  applies prefs immediately, so the team-arrangement lookup ran against an
 *  empty areas list and missed. (The viewless '#/p/53' twin needs none of this:
 *  the router PARKS it and re-runs applyPrefs once areasLoaded flips —
 *  router.ts pendingView — which sees the team default through the normal
 *  path.) Once /areas lands, nudge each arrangement key ONLY when no personal
 *  stored value exists for it AND the user hasn't moved the control off its
 *  reset default while /areas was in flight. A pure filter-store nudge: no
 *  localStorage write (a team default must never masquerade as a personal
 *  pref), no navigation, no nav.tx (groupBy/sortBy aren't in the URL — the
 *  router has nothing to reconcile). */
function applyLateTeamArrangement(): void {
  const { areas, selectedAreaId } = useAreaStore.getState();
  if (selectedAreaId === null) return;
  const area = areas.find((a) => a.id === selectedAreaId);
  if (!area || (!area.defaultGroupBy && !area.defaultSortBy)) return;
  let hasGroupBy = false;
  let hasSortBy = false;
  try {
    const p = JSON.parse(localStorage.getItem(`cgp:area:${selectedAreaId}:prefs`) ?? '{}') as { groupBy?: string; sortBy?: string };
    hasGroupBy = !!p.groupBy;
    hasSortBy = !!p.sortBy;
  } catch { /* best-effort — treat as absent */ }
  const f = useFilterStore.getState();
  // `synced` moves WITH the nudge, so the Layout echo this triggers compares
  // equal in persistPrefs and writes nothing.
  if (!hasGroupBy && area.defaultGroupBy && f.groupBy === RESET_GROUP_BY) {
    f.setGroupBy(area.defaultGroupBy);
    if (synced?.areaId === selectedAreaId) synced.groupBy = area.defaultGroupBy;
  }
  if (!hasSortBy && area.defaultSortBy && f.sortBy === RESET_SORT_BY) {
    f.setSortBy(area.defaultSortBy);
    if (synced?.areaId === selectedAreaId) synced.sortBy = area.defaultSortBy;
  }
}
